import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  AnalyticsEventNames,
  Categories,
  DataLayerEventName,
  pushToAdobeDataLayer,
} from '@app/services/adobeDataLayer';
import { getBroadbandLabelAttributes } from '@app/redux/broadbandLabel/broadbandLabelSlice';
import {
  ChangePlanSteps,
  getPlans,
  refetchPlans,
  selectChangePlan,
  setChangePlanError,
  setChangePlanStep,
} from '@app/redux/changePlan';
import { getCharacteristics, getPrice, selectCurrentPlanPrice, selectPlanCharacteristics } from '@app/redux/plan';

import OptionalServiceWarning from './containers/OptionalServiceWarning';
import OrderOutcome from './containers/OrderOutcome';
import OrderReview from './containers/OrderReview';
import PlanSelection from './containers/PlanSelection';
import ScheduleInstallation from './containers/ScheduleInstallation';
import { NavigationHandlers } from './types';

const ChangePlan = ({ navigateToPreviousPage, navigateToOverview }: NavigationHandlers) => {
  const dispatch = useDispatch();
  const { error, step, planOffersData, orderSuccess, hasPendingTransition } = useSelector(selectChangePlan);
  const {
    characteristics: { id: productTypeId },
  } = useSelector(selectPlanCharacteristics);

  const { price } = useSelector(selectCurrentPlanPrice);

  useEffect(() => {
    if (error || hasPendingTransition || planOffersData?.length === 0) {
      dispatch(refetchPlans());
    } else {
      dispatch(getPlans());
    }

    dispatch(getBroadbandLabelAttributes());

    if (!productTypeId) dispatch(getCharacteristics());

    if (!price) dispatch(getPrice());

    pushToAdobeDataLayer({
      event: DataLayerEventName.FORM_START,
      event_name: DataLayerEventName.FORM_START,
      event_label: AnalyticsEventNames.ChangePlanInitiated,
      event_category: Categories.ChangePlan,
    });

    return () => {
      dispatch(setChangePlanStep(ChangePlanSteps.PLAN_SELECTION));
    };
  }, []);

  useEffect(() => {
    if (orderSuccess && !planOffersData) {
      dispatch(refetchPlans());
    }
  }, [orderSuccess, planOffersData, dispatch]);

  const handleBackNavigation = () => {
    dispatch(setChangePlanStep(ChangePlanSteps.PLAN_SELECTION));
    dispatch(setChangePlanError(null));
  };

  switch (step) {
    case ChangePlanSteps.ORDER_REVIEW:
      return <OrderReview handleBackNavigation={handleBackNavigation} />;
    case ChangePlanSteps.OPTIONAL_SERVICE_WARNING:
      return <OptionalServiceWarning handleBackNavigation={handleBackNavigation} />;
    case ChangePlanSteps.ORDER_OUTCOME:
      return <OrderOutcome navigateToOverview={navigateToOverview} />;
    case ChangePlanSteps.SELECT_SCHEDULE:
      return <ScheduleInstallation />;
    case ChangePlanSteps.PLAN_SELECTION:
    default:
      return <PlanSelection navigateToPreviousPage={navigateToPreviousPage} />;
  }
};

export default ChangePlan;
